import { computed, type Ref } from 'vue';
import type { MindMapNode, LayoutNode, Connection } from '../types';

const NODE_WIDTH = 120;
const NODE_HEIGHT = 36;
const H_GAP = 80;
const V_GAP = 20;

const STEP_X = NODE_WIDTH + H_GAP;

interface MeasuredNode {
  node: LayoutNode;
  totalHeight: number;
  // contour[depth] = { min, max } relative y range of nodes at that depth within this subtree
  contour: Map<number, { min: number; max: number }>;
}

function getContourBounds(contour: MeasuredNode['contour']): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const range of contour.values()) {
    min = Math.min(min, range.min);
    max = Math.max(max, range.max);
  }
  return { min, max };
}

function measureSubtree(node: MindMapNode, depth: number = 0): MeasuredNode {
  const ln: LayoutNode = {
    ...node,
    x: 0,
    y: 0,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    children: [],
    depth,
  };

  const contour = new Map<number, { min: number; max: number }>();
  // This node itself contributes to the contour at its own depth
  contour.set(depth, { min: 0, max: NODE_HEIGHT });

  if (!node.children || node.children.length === 0 || node.collapsed) {
    return { node: ln, totalHeight: NODE_HEIGHT, contour };
  }

  // Recursively measure children
  const measuredChildren = node.children.map((c) => measureSubtree(c, depth + 1));
  ln.children = measuredChildren.map((m) => m.node);

  // Arrange children vertically by their actual subtree bounds.
  // A single direct child should sit exactly to the right of its parent, even
  // when that child owns a taller subtree.
  let totalChildrenHeight;
  if (measuredChildren.length === 1) {
    measuredChildren[0]!.node.y = 0;
    measuredChildren[0]!.node.x = STEP_X;
    totalChildrenHeight = measuredChildren[0]!.totalHeight;
  } else {
    let currentBottom = 0;
    for (const child of measuredChildren) {
      const childBounds = getContourBounds(child.contour);
      child.node.y = currentBottom - childBounds.min;
      child.node.x = STEP_X;
      currentBottom = child.node.y + childBounds.max + V_GAP;
    }

    // Center children vertically around this node
    totalChildrenHeight = currentBottom - V_GAP;
    const offset = totalChildrenHeight / 2 - NODE_HEIGHT / 2;
    for (const child of measuredChildren) {
      child.node.y -= offset;
    }
  }

  // Shift contour entries after centering
  for (const child of measuredChildren) {
    for (const [d, range] of child.contour) {
      const adjusted = { min: range.min + child.node.y, max: range.max + child.node.y };
      const existing = contour.get(d);
      if (existing) {
        contour.set(d, {
          min: Math.min(existing.min, adjusted.min),
          max: Math.max(existing.max, adjusted.max),
        });
      } else {
        contour.set(d, adjusted);
      }
    }
  }

  // Update self contour at depth to reflect adjusted parent position
  contour.set(depth, { min: ln.y, max: ln.y + NODE_HEIGHT });

  const bounds = getContourBounds(contour);
  const totalHeight = Math.max(NODE_HEIGHT, bounds.max - bounds.min, totalChildrenHeight);
  return { node: ln, totalHeight, contour };
}

export function useLayout(root: Ref<MindMapNode | null>) {
  const layoutData = computed(() => {
    if (!root.value) return { nodes: [] as LayoutNode[], connections: [] as Connection[] };

    const result = measureSubtree(root.value);
    const nodes: LayoutNode[] = [];

    function flatten(n: LayoutNode, parent?: LayoutNode) {
      if (parent) {
        n.x += parent.x;
        n.y += parent.y;
        n.parentX = parent.x + NODE_WIDTH;
        n.parentY = parent.y + NODE_HEIGHT / 2;
      }
      nodes.push(n);
      for (const c of n.children) {
        flatten(c, n);
      }
    }

    flatten(result.node);

    const connections: Connection[] = [];
    for (const n of nodes) {
      if (n.parentX !== undefined && n.parentY !== undefined) {
        connections.push({
          from: { x: n.parentX, y: n.parentY },
          to: { x: n.x, y: n.y + NODE_HEIGHT / 2 },
        });
      }
    }

    return { nodes, connections, totalWidth: getTotalWidth(result.node), totalHeight: getTotalHeight(nodes) };
  });

  return layoutData;
}

function getTotalWidth(node: LayoutNode): number {
  let maxX = node.x + NODE_WIDTH;
  for (const c of node.children) {
    maxX = Math.max(maxX, getTotalWidth(c));
  }
  return maxX;
}

function getTotalHeight(nodes: LayoutNode[]): number {
  let maxY = 0;
  let minY = Infinity;
  for (const n of nodes) {
    if (n.y < minY) minY = n.y;
    if (n.y + NODE_HEIGHT > maxY) maxY = n.y + NODE_HEIGHT;
  }
  return maxY - minY;
}
